// Renewpawner.jsx — zoom-safe, matches PawnerForm compact layout (DOB hardened to dd/mm/yyyy UI)
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import usePersonScreening from "../hooks/usePersonScreening";
import ScreeningWarning from "./ScreeningWarning";
import "./PawnerForm.css";  // reuse the compact/zoom-safe CSS


const cleanGoldNumber = (value) => {
  if (value === null || value === undefined || value === "") return "0";
  const cleaned = String(value).replace(/,/g, "").trim();
  return cleaned || "0";
};

const buildPawnGoldPayload = (pledgeNo, details) => {
  if (!pledgeNo || !details?.weights) return null;

  const weights = details.weights || {};
  const prices = details.prices || {};

  return {
    pledgeNo,

    gold1Qty: cleanGoldNumber(weights["999"]),
    gold2Qty: cleanGoldNumber(weights["24K"]),
    gold3Qty: cleanGoldNumber(weights["23K"]),
    gold4Qty: cleanGoldNumber(weights["22K"]),
    gold5Qty: cleanGoldNumber(weights["20K"]),
    gold6Qty: cleanGoldNumber(weights["18K"]),
    gold7Qty: cleanGoldNumber(weights["14K"]),
    gold8Qty: cleanGoldNumber(weights["9K"]),
    gold9Qty: cleanGoldNumber(weights["STONE"]),

    gold1Price: cleanGoldNumber(prices["999"]),
    gold2Price: cleanGoldNumber(prices["24K"]),
    gold3Price: cleanGoldNumber(prices["23K"]),
    gold4Price: cleanGoldNumber(prices["22K"]),
    gold5Price: cleanGoldNumber(prices["20K"]),
    gold6Price: cleanGoldNumber(prices["18K"]),
    gold7Price: cleanGoldNumber(prices["14K"]),
    gold8Price: cleanGoldNumber(prices["9K"]),
    gold9Price: cleanGoldNumber(prices["STONE"]),

    goldCode: prices.goldCode ?? details.goldCode ?? null,
    valAddAmt: cleanGoldNumber(details.valAdd),
  };
};

const savePawnGoldBreakdown = async ({ lookupPledgeNo, finalPledgeNo, transactionData }) => {
  const details = transactionData?.goldDetails;

  // No gold modal data was loaded/edited, so do not risk saving stale data.
  if (!details?.weights) return;

  const lookup = (lookupPledgeNo || finalPledgeNo || "").trim();
  const finalNo = (finalPledgeNo || lookupPledgeNo || "").trim();
  const payload = buildPawnGoldPayload(finalNo, details);

  if (!lookup || !payload) return;

  await axios.put("https://www.lianfoong.com/api/pawn-gold/pledge", payload, {
    params: { pledgeNo: lookup },
    withCredentials: true,
  });
};


/* ---------------- Date helpers: force dd/mm/yyyy in UI, ISO (yyyy-MM-dd) on wire ---------------- */
const pad2 = (n) => String(n).padStart(2, "0");

/** Parse user-entered date to ISO yyyy-MM-dd. Accepts:
 *  - dd/mm/yyyy or d/m/yyyy
 *  - yyyy-mm-dd or yyyy/mm/dd
 *  Falls back to a permissive parse pinned to UTC (then returns ISO).
 */
function parseToIso(dateText) {
  if (!dateText) return "";
  const s = String(dateText).trim();

  // dd/mm/yyyy or d/m/yyyy
  let m = s.match(/^(\d{1,2})[\/](\d{1,2})[\/](\d{4})$/);
  if (m) {
    const d = parseInt(m[1], 10), mo = parseInt(m[2], 10), y = parseInt(m[3], 10);
    if (mo >= 1 && mo <= 12) {
      const dt = new Date(Date.UTC(y, mo - 1, d));
      if (dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d) {
        return `${y}-${pad2(mo)}-${pad2(d)}`;
      }
    }
  }

  // yyyy-mm-dd or yyyy/mm/dd
  m = s.match(/^(\d{4})[-\/](\d{2})[-\/](\d{2})$/);
  if (m) {
    const y = parseInt(m[1], 10), mo = parseInt(m[2], 10), d = parseInt(m[3], 10);
    const dt = new Date(Date.UTC(y, mo - 1, d));
    if (dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d) {
      return `${y}-${pad2(mo)}-${pad2(d)}`;
    }
  }

  // Last resort: permissive parse pinned to UTC
  const dt = new Date(`${s}T00:00:00Z`);
  if (!isNaN(dt.getTime())) {
    const y = dt.getUTCFullYear(), mo = dt.getUTCMonth() + 1, d = dt.getUTCDate();
    return `${y}-${pad2(mo)}-${pad2(d)}`;
  }
  return "";
}

function isoToDdMmYyyy(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

/* ---------------- Misc helpers ---------------- */
const toTitle = (s) => (s || "").toString().toUpperCase();

export default function Renewpawner() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasFetched = useRef(false);

  const { transactionData, oldReceiptNo, amountCollected } = location.state || {};

  // Form state
  const [nric, setNric] = useState("");
  const [name, setName] = useState("");
  const [race, setRace] = useState("");
  const [sex, setSex] = useState("");
  const [nationality, setNationality] = useState("");
  const [sgAddr, setSgAddr] = useState("");
  const [foreignAddr, setForeignAddr] = useState("");
  // DOB is split into display (dd/mm/yyyy) and wire (yyyy-MM-dd)
  const [dobIso, setDobIso] = useState("");
  const [dobText, setDobText] = useState("");
  const [contactNoMobile, setContactNoMobile] = useState("");
  const [blacklisted, setBlacklisted] = useState(false);

  const screening = usePersonScreening(name, dobIso, { requireDob: true, context: JSON.stringify([transactionData, nric, name, race, sex, nationality, sgAddr, foreignAddr, dobIso, contactNoMobile, blacklisted]) });

  // Esc → dashboard
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") navigate("/dashboard"); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  // Prefill from old receipt
  useEffect(() => {
    if (!oldReceiptNo || hasFetched.current) return;
    hasFetched.current = true;
    axios
      .get("https://www.lianfoong.com/api/pawn-transactions/getPawnerDetails", {
        params: { oldReceiptNo }, withCredentials: true,
      })
      .then(({ data }) => hydrateFromCustomer(data))
      .catch((err) => console.error("fetch by oldReceiptNo failed:", err));
  }, [oldReceiptNo]);

  // Lookup by NRIC
  const loadByNric = async (id) => {
    const ic = (id || "").trim().toUpperCase();
    if (!ic) return;
    try {
      const { data } = await axios.get(
        "https://www.lianfoong.com/api/customers/by-nric",
        { params: { nirc: ic }, withCredentials: true }
      );
      hydrateFromCustomer(data, ic);
    } catch {
      try {
        const { data } = await axios.post(
          "https://www.lianfoong.com/api/customers/nric",
          { nirc: ic }, { withCredentials: true }
        );
        hydrateFromCustomer(data, ic);
      } catch {
        setNric(ic);
        setName(""); setRace(""); setSex(""); setNationality("");
        setSgAddr(""); setForeignAddr("");
        setContactNoMobile(""); setBlacklisted(false);
        setDobIso(""); setDobText("");
      }
    }
  };

  const hydrateFromCustomer = (data, fallbackIc) => {
    setNric(data?.nirc || fallbackIc || "");
    setName(toTitle(data?.name));
    setRace(toTitle(data?.race));
    setSex(toTitle(data?.sex));
    setNationality(toTitle(data?.nationality));
    setSgAddr(toTitle(data?.sgAddr));
    setForeignAddr(toTitle(data?.foreignAddr));
    setContactNoMobile(data?.contactNoMobile || "");
    setBlacklisted(Boolean(data?.blacklisted));

    const rawDob = data?.dob || "";
    const iso = /^\d{4}-\d{2}-\d{2}$/.test(rawDob) ? rawDob : parseToIso(rawDob);
    setDobIso(iso);
    setDobText(isoToDdMmYyyy(iso));
  };

  const onNricKeyDown = (e) => { if (e.key === "Enter") { e.preventDefault(); loadByNric(nric); } };

  const handleSubmit = async () => {
    if (!transactionData) {
      alert("Transaction details missing. Please go back to previous step.");
      return;
    }
    if (amountCollected == null || String(amountCollected).trim() === ""
        || !Number.isFinite(Number(amountCollected)) || Number(amountCollected) < 0) {
      alert("Renewal interest is missing. Please return to Renew and verify the collected amount.");
      return;
    }
    if (!(await screening.confirmBeforeProceed())) return;
    const enteredHandler = prompt("Please enter your handler code:");
    if (!enteredHandler) { alert("Handler code is required!"); return; }

    try {
      // 1) Upsert customer (send ISO DOB only)
      await axios.post("https://www.lianfoong.com/api/customers", {
        nirc: nric, name, race, sex, nationality, sgAddr, foreignAddr,
        dob: dobIso || null,  // <--- ISO format
        contactNoMobile, blacklisted,
      }, { withCredentials: true });

      // 2) Create renewal + get PDF (server returns new receipt no in headers)
      const res = await axios.post(
        "https://www.lianfoong.com/api/pawn-transactions/renewal",
        {
          transaction: { ...transactionData, pawnerNirc: nric, oldReceiptNo, handler: enteredHandler },
          amountCollected: Number(amountCollected ?? 0),
        },
        { responseType: "blob", withCredentials: true }
      );

      // 3) Extract the new receipt number from headers (case-insensitive)
      const h = res?.headers || {};
      let receiptNo = h["x-receipt-no"] || h["X-Receipt-No"];
      if (!receiptNo) {
        const cdHeader = h["content-disposition"] || h["Content-Disposition"] || "";
        const match = cdHeader.match(/receipt_([A-Za-z])(\d{2})(\d{2})(\d{4})/);
        if (match) receiptNo = `${match[1].toUpperCase()}/${match[2]}${match[3]}/${match[4]}`;
      }
      if (!receiptNo) {
        alert("Receipt number missing. Please ensure server exposes 'X-Receipt-No' via CORS (Access-Control-Expose-Headers).");
        return;
      }

      // 4) Attach NEW receipt number to the latest payment for the OLD receipt (INSTORE)
      try {
        await axios.patch(
          "https://www.lianfoong.com/api/renewals/payments/link-by-old",
          { newReceiptNo: receiptNo, source: "INSTORE" },
          { params: { oldReceiptNo }, withCredentials: true }
        );
      } catch (e) {
        const status = e?.response?.status;
        if (status === 403 || status === 404) {
          console.warn("Attach skipped:", e?.response?.data || e.message);
        } else {
          console.warn("Attach failed:", e);
        }
      }

      // 5) Save pawn-gold breakdown for the NEW pledge number
      try {
        await savePawnGoldBreakdown({
          lookupPledgeNo: receiptNo,
          finalPledgeNo: receiptNo,
          transactionData,
        });
      } catch (e) {
        console.warn("Saving pawn-gold failed (non-fatal):", e);
      }

      // 6) Download PDF
      {
        const blob = new Blob([res.data], { type: "application/pdf" });
        const cd2 = (res.headers["content-disposition"] || "");
        const m2 = cd2.match(/filename=([^;]+)/i);
        const filename = m2 ? m2[1].replace(/"/g, "") : `receipt_${Date.now()}.pdf`;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
      }

      // 7) Deactivate old ticket (after successful renewal)
      await axios.put(
        "https://www.lianfoong.com/api/pawn-transactions/deactivate-old-ticket",
        null,
        { params: { oldReceiptNo }, withCredentials: true }
      );

      alert("Renewal processed successfully! PDF downloaded.");
      navigate("/dashboard");
    } catch (error) {
      console.error("Renewal failed:", error);
      alert("Failed to complete renewal.");
    }
  };

  return (
    <div className="pf-page">
      <div className="pawner-form pawner-form--wide pf-compact">
        <div className="pf-header">
          <h2 className="pf-title">Pawner Record (Renew)</h2>
        </div>

        <div className="pf-body-noscroll">
          <div className="pf-grid-12">
            <div className="pf-field col-4">
              <label className="pf-label">NRIC No</label>
              <input
                className="pf-input"
                value={nric}
                onChange={(e) => setNric(e.target.value.toUpperCase())}
                onKeyDown={onNricKeyDown}
                onBlur={() => loadByNric(nric)}
              />
            </div>

            <div className="pf-field col-8">
              <label className="pf-label">Name</label>
              <input className="pf-input" value={name} onChange={(e) => setName(toTitle(e.target.value))} />
            </div>

            <div className="pf-field col-4">
              <label className="pf-label">Race</label>
              <input className="pf-input" value={race} onChange={(e) => setRace(toTitle(e.target.value))} />
            </div>

            <div className="pf-field col-4">
              <label className="pf-label">Sex</label>
              <input className="pf-input" value={sex} onChange={(e) => setSex(toTitle(e.target.value))} />
            </div>

            <div className="pf-field col-4">
              <label className="pf-label">Nationality</label>
              <input className="pf-input" value={nationality} onChange={(e) => setNationality(toTitle(e.target.value))} />
            </div>

            <div className="pf-field col-12">
              <label className="pf-label">Address (Singapore)</label>
              <textarea className="pf-input pf-textarea" value={sgAddr} onChange={(e) => setSgAddr(toTitle(e.target.value))} />
            </div>

            <div className="pf-field col-6">
              <label className="pf-label">Birthdate (DD/MM/YYYY)</label>
              <input
                type="text"
                className="pf-input"
                placeholder="DD/MM/YYYY"
                value={dobText}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^\d/]/g, "").slice(0, 10);
                  setDobText(v);
                  const iso = parseToIso(v);
                  setDobIso(iso);
                }}
                onBlur={() => {
                  const iso = parseToIso(dobText);
                  setDobIso(iso);
                  setDobText(iso ? isoToDdMmYyyy(iso) : "");
                }}
              />
            </div>

            <div className="pf-field col-6">
              <label className="pf-label">Contact No (Mobile)</label>
              <input className="pf-input" value={contactNoMobile} onChange={(e) => setContactNoMobile(e.target.value)} />
            </div>

            <div className="pf-field col-12 pf-field--message"><ScreeningWarning screening={screening} /></div>

          </div>
        </div>

        <div className="pf-footer-nosticky">
          <button type="button" className="pf-btn pf-btn--ghost" onClick={() => navigate("/dashboard")}>
            Back
          </button>
          <button type="button" className="pf-btn pf-btn--danger" onClick={() => navigate("/dashboard")}>
            Cancel
          </button>
          <button type="button" className="pf-btn pf-btn--primary" onClick={handleSubmit}>
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}
